import React from 'react';

import { Redirect, Switch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PrivateRoute from './navigation/PrivateRoute';
import AuthRoute from './navigation/AuthRoute';
import { useAllAvailableRoutes } from './routes/routes';
import NotFoundPage from './ErrorPages/NotFoundPage/NotFoundPage';
import { useAvailableAuthRoutes } from './routes/auth-routes';
import { useAvailablePublicRoutes } from '@ApplicationWide/routes/public-routes';
import { useAppConfig } from './AppConfig';
import getIsSignUpSite from '@ApplicationWide/getIsSignUpSite';
import ErrorAwareRoute from '@Generic/SaplingRoute/ErrorAwareRoute';

const NotAllowedPage = React.lazy(() => import('./ErrorPages/NotAllowedPage/NotAllowedPage'));
const ManagerFormContainer = React.lazy(() =>
  import('../pages/HomePage/HomePageTabs/TeamTab/prepareTableColumns/Modals/ManagerFormContainer'),
);
const ManagerForm = React.lazy(() =>
  import('../pages/HomePage/HomePageTabs/TeamTab/prepareTableColumns/Modals/ManagerForm'),
);
const SignupPage = React.lazy(() => import('../pages/Auth/SignUpPage/SignupPage'));

// if the route is a simple string path, then it's a valid key, but routes
// can also be an array, in which case we need to convert to string
const getRouteKey = (routeConfig) =>
  typeof routeConfig.route === 'string' ? routeConfig.route : routeConfig.route.join(',');

export default function MainRoutes() {
  const config = useAppConfig();
  const routes = useAllAvailableRoutes();
  const publicRoutes = useAvailablePublicRoutes();
  const authRoutes = useAvailableAuthRoutes();
  const isSignUpSite = getIsSignUpSite();

  if (isSignUpSite) {
    return (
      <Switch>
        <ErrorAwareRoute path='/signup' to='/signup'>
          <SignupPage />
        </ErrorAwareRoute>
        <Redirect to='/signup' />
      </Switch>
    );
  }

  return (
    <>
      <Helmet>
        <title>{config?.company?.baseDetails?.name}</title>
      </Helmet>
      <Switch>
        {publicRoutes.map((route) => (
          <ErrorAwareRoute
            key={route.route}
            exact
            path={route.route}
            component={route.component}
            route={route}
            {...route.extraProps}
          />
        ))}
        {routes.map((route) => (
          <PrivateRoute
            key={getRouteKey(route)}
            exact
            hasNavBar={route.hasNavBar}
            path={route.route}
            component={route.component}
            route={route}
            {...route.extraProps}
          />
        ))}
        {authRoutes.map((routeConfig) => (
          <AuthRoute
            key={getRouteKey(routeConfig)}
            exact
            path={routeConfig.route}
            component={routeConfig.component}
            {...routeConfig.extraProps}
          />
        ))}
        <ErrorAwareRoute
          exact={true}
          path='/manager_form/:employee_id/:token'
          render={(props) => <ManagerForm {...props} />}
        />
        <PrivateRoute exact={true} path='/manager_form' component={ManagerFormContainer} />
        <PrivateRoute exact path='/permission-denied' hasNavBar component={NotAllowedPage} />
        <PrivateRoute hasNavBar component={NotFoundPage} />
      </Switch>
    </>
  );
}
